import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { QueryKey } from "@tanstack/react-query";

import { AUTO_REFRESH_DEFAULTS } from "@/constants/auto-refresh.constants";
import { useAutoRefresh, type IUseAutoRefreshResult } from "@/hooks/use-auto-refresh";
import { isDefined, isEmpty } from "@/lib/common";

export interface IAutoRefreshRegistration {
  /** Countdown length in whole seconds — a per-page constant, e.g. `TICKETS_PAGE_AUTO_REFRESH_INTERVAL_SECONDS`. */
  intervalSeconds: number;
  /** Query keys invalidated on each tick — every active match refetches silently in the background. */
  queryKeys: QueryKey[];
  enabled?: boolean;
}

interface IAutoRefreshRegistryContextValue {
  registerSuspend: (id: string, isOpen: boolean) => void;
  registerConfig: (config: IAutoRefreshRegistration | null) => void;
}

/**
 * Split from the state context on purpose: the countdown value changes several times a second,
 * so every consumer of it re-renders at that rate. Pages and dialogs only ever need the two
 * register callbacks, which are stable for the life of the provider — subscribing them here
 * keeps the ticking countdown from re-rendering half the page.
 */
const AutoRefreshRegistryContext = createContext<IAutoRefreshRegistryContextValue | null>(null);
const AutoRefreshStateContext = createContext<IUseAutoRefreshResult | null>(null);

/**
 * Mounted once in the app shell (`pages/dashboard.tsx`), above the router `Outlet`. It holds no
 * page knowledge of its own: a page opts in by calling `useRegisterAutoRefresh(...)`, and the
 * shell's `<AutoRefreshIndicator />` renders nothing until one does.
 */
export const AutoRefreshProvider = ({ children }: { children: ReactNode }) => {
  const [openSuspendIds, setOpenSuspendIds] = useState<Set<string>>(() => new Set());
  const [config, setConfig] = useState<IAutoRefreshRegistration | null>(null);

  const registerSuspend = useCallback((id: string, isOpen: boolean) => {
    setOpenSuspendIds((current) => {
      const isAlreadyTracked = current.has(id);
      if (isOpen === isAlreadyTracked) return current;

      const next = new Set(current);
      if (isOpen) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const registerConfig = useCallback((next: IAutoRefreshRegistration | null) => setConfig(next), []);

  const isRegistered = isDefined(config);

  const autoRefresh = useAutoRefresh({
    intervalSeconds: config?.intervalSeconds ?? AUTO_REFRESH_DEFAULTS.FALLBACK_INTERVAL_SECONDS,
    queryKeys: config?.queryKeys ?? [],
    enabled: isRegistered && (config?.enabled ?? AUTO_REFRESH_DEFAULTS.ENABLED),
    isSuspendedExternally: !isEmpty(Array.from(openSuspendIds)),
  });

  const registryValue = useMemo<IAutoRefreshRegistryContextValue>(
    () => ({ registerSuspend, registerConfig }),
    [registerSuspend, registerConfig],
  );

  return (
    <AutoRefreshRegistryContext.Provider value={registryValue}>
      <AutoRefreshStateContext.Provider value={isRegistered ? autoRefresh : null}>
        {children}
      </AutoRefreshStateContext.Provider>
    </AutoRefreshRegistryContext.Provider>
  );
};

/**
 * Countdown/ring/pause state, or `null` when the current page has not opted in. Used by
 * `AutoRefreshIndicator`, and by the shell to decide whether to reserve room for the pill.
 */
export const useAutoRefreshState = (): IUseAutoRefreshResult | null =>
  useContext(AutoRefreshStateContext);

/**
 * A page opts into auto-refresh by calling this with its own interval and query keys. The pill
 * appears in the shell's top-right slot for as long as the page is mounted, and the registration
 * clears on unmount so the next route starts with no auto-refresh unless it asks for one.
 *
 * Keep `queryKeys` referentially stable (`useMemo`) — it is part of the registration identity.
 */
export const useRegisterAutoRefresh = (registration: IAutoRefreshRegistration): void => {
  const registry = useContext(AutoRefreshRegistryContext);
  const { intervalSeconds, queryKeys, enabled } = registration;

  useEffect(() => {
    if (!isDefined(registry)) return;

    registry.registerConfig({ intervalSeconds, queryKeys, enabled });
    return () => registry.registerConfig(null);
  }, [registry, intervalSeconds, queryKeys, enabled]);
};

let suspendRegistrationCounter = 0;

/**
 * Have any dialog/drawer/sheet call this with its own `isOpen` state to pause auto-refresh while
 * it is open, and resume cleanly when it closes. A no-op outside an `AutoRefreshProvider`, so it
 * is safe to call from a component reused on pages that never opt in.
 */
export const useSuspendAutoRefresh = (isOpen: boolean): void => {
  const registry = useContext(AutoRefreshRegistryContext);
  const idRef = useRef<string | undefined>(undefined);
  if (!isDefined(idRef.current)) {
    suspendRegistrationCounter += 1;
    idRef.current = `auto-refresh-suspend-${suspendRegistrationCounter}`;
  }

  useEffect(() => {
    if (!isDefined(registry) || !isDefined(idRef.current)) return;

    const id = idRef.current;
    registry.registerSuspend(id, isOpen);
    return () => registry.registerSuspend(id, false);
  }, [registry, isOpen]);
};
